import {
    put, all, takeLatest,
} from 'redux-saga/effects';
import _ from 'underscore';

import {
    getCookies,
    setCookie,
    getCookie,
    deleteCookie,
} from 'helpers/CookiesHelpers';

import {
    INIT_CMP_APP,
    initCMPSuccess,
    acceptAllCookies,
    hideBanner,
    setAcceptedCookies,
} from '../reduxModules/ConsentManagementPlatform';

// The duration of the consent validity is set to 13 months
export const CONSENT_VALIDITY_DURATION = 13;

function isCurrentUserABot() {
    const botPattern = /bot|Google-Structured-Data-Testing-Tool/gi;
    return botPattern.test(navigator.userAgent);
}

function getFromLocalStorageOrCookie(key) {
    // Usage of try/catch to avoid console errors related to the desactivation of localstorage
    // and/or cookies
    try {
        return window.localStorage.getItem(key);
    } catch (localStorageNotAvailable) {
        try {
            return getCookie(key);
        } catch (cookieNotAvailable) {
            // Cookie and localstorage are disabled/unavailable
            return null;
        }
    }
}

export function* callAction() {
    const timerFromStorage = getFromLocalStorageOrCookie('ypCmpTimer');

    // Handle the reset after 13 months
    const today = Math.floor(Date.now() / 1000);
    if (timerFromStorage && today >= timerFromStorage) {
        // Reset CMP Data
        try {
            deleteCookie('ypCmpTimer');
            deleteCookie('YPConsent');
        } catch (cookieNotAvailable) {
            // Cookies are not available
        }
        try {
            window.localStorage.removeItem('ypCmpTimer');
        } catch (localStorageNotAvailable) {
            // localStorage is not available
        }
    }

    // Automatically accept all the cookies when a bot is crawling us
    // We "need" Trustpilot cookie to get the json-ld related tags and display
    // the recommandation stars on search engines
    // See https://support.trustpilot.com/hc/en-us/articles/231993108-Walkthrough-Using-Product-Review-TrustBoxes-to-get-rich-snippets-for-SEO
    // And YP-4971 for more information
    if (isCurrentUserABot()) {
        yield all([
            put(acceptAllCookies()),
            put(hideBanner()),
        ]);
        return;
    }
    // Analytics cookies can be activated by default without consent since
    // their configuration is compliant with the CNIL recommandations
    // We do this at the first ever launch of the app
    if (document.cookie.indexOf('YPConsent') === -1) {
        setCookie('YPConsent', 'MATOMO_EXEMPTED_CONSENT', CONSENT_VALIDITY_DURATION);
        yield put(setAcceptedCookies({ id: 'MATOMO_EXEMPTED_CONSENT' }));
    }
    const cookies = getCookies();
    // If  cookies are not readable, getCookies will return an empty object. In this case,
    // we need to hide the banner, and refuse all cookies, which will prevent the execution
    // of all related js scripts
    if (Object.keys(cookies).length === 0) {
        yield all([
            put(hideBanner()),
            put(setAcceptedCookies([])),
        ]);
    // To decide weither the user has already made a "choice", we use the timerFromStorage variable
    // which is set in 3 cases :
    // 1) accept all from banner,
    // 2) personalized choice,
    } else {
        let cookieYPConsentAsArray = [];
        // We need to assign a value to cookieYPConsentAsArray only if cookies.YPConsent is defined
        // ie if at least one cookie has been accepted
        if (cookies.YPConsent) {
            cookieYPConsentAsArray = cookies.YPConsent.split(',');
        }
        const acceptedCookiesFromDocument = _.map(cookieYPConsentAsArray, (cookie) => ({
            id: cookie,
        }));
        // Initialize app based on previous choices stored in the browser cookies
        yield all([
            put(setAcceptedCookies(acceptedCookiesFromDocument)),
        ]);
        if (timerFromStorage) {
            yield put(hideBanner());
        }
    }
    yield put(initCMPSuccess());
}

export function* watch() {
    yield takeLatest(INIT_CMP_APP, callAction);
}
